import React from "react";
import { Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import { GroupedNav } from "@/components/nav/GroupedNav";
import { MobileTopBar, useMobileMenu } from "@/components/MobileTopBar";
import { useAuth } from "@/AuthContext";
import { usePerms } from "@/hooks/usePerms";
import { LayoutDashboard, Building2, LogOut, CreditCard, GraduationCap, Quote, Inbox, Megaphone, LifeBuoy, Flag, HeartHandshake, Activity, MessageSquare, Shield, ShieldCheck, Ban, Mail, Zap, AlertTriangle, LayoutGrid, Link2, Languages, Tag, BellRing, Users, Lock, Scale, KeyRound, Gavel, Receipt, Landmark, Briefcase, Globe, Phone, Sparkles, Store, TreePine } from "lucide-react";
import { Button } from "@/components/ui/button";
import AdminGlobalSearch from "@/components/AdminGlobalSearch";
import { PushEnableBanner } from "@/components/PushNotifications";
import { PortalIncidentBanner } from "@/components/IncidentBanner";
import { LanguageCurrencySwitcher } from "@/components/LanguageCurrencySwitcher";

const TOP_NAV = [
  { to: "/admin", label: "Vue d'ensemble", icon: LayoutDashboard, end: true },
  { to: "/admin/widgets", label: "Dashboard modulable", icon: LayoutGrid },
];

/** Catégories du menu admin (Phase 74). Les libellés/testids des entrées sont inchangés. */
const GROUPS = [
  { key: "clients", label: "Clients & inscriptions", icon: Building2, items: [
    { to: "/admin/entreprises", label: "Entreprises", icon: Building2 },
    { to: "/admin/syndicats", label: "Syndicats", icon: Flag },
    { to: "/admin/associations", label: "Associations / ONG", icon: HeartHandshake },
    { to: "/admin/inscriptions", label: "Inscriptions", icon: Inbox },
    { to: "/admin/cancellations", label: "Résiliations", icon: Ban },
    { to: "/admin/web", label: "Sites web clients", icon: Globe },
  ] },
  { key: "paiements", label: "Facturation & paiements", icon: Receipt, items: [
    { to: "/admin/billing", label: "Facturation PAG", icon: Receipt },
    { to: "/admin/paypal", label: "Paiement PayPal", icon: CreditCard },
    { to: "/admin/stripe", label: "Paiement Stripe", icon: CreditCard },
    { to: "/admin/stripe-connect", label: "Stripe Connect", icon: Landmark },
    { to: "/admin/promotions", label: "Promotions", icon: Tag },
  ] },
  { key: "recrutement", label: "Recrutement & partenaires", icon: Briefcase, items: [
    { to: "/admin/recrutement", label: "Recrutement (PAG JOB)", icon: Briefcase },
    { to: "/admin/business-partners", label: "Business Partners", icon: HeartHandshake },
  ] },
  { key: "communication", label: "Communication", icon: Mail, items: [
    { to: "/admin/notifications", label: "Notifications & Mail", icon: BellRing },
    { to: "/admin/emails", label: "Emails Resend", icon: Mail },
    { to: "/admin/sendgrid", label: "SendGrid (Email)", icon: Mail },
    { to: "/admin/twilio", label: "SMS (Twilio)", icon: MessageSquare },
    { to: "/admin/talk", label: "PAG Talk (téléphonie)", icon: Phone },
    { to: "/admin/announcements", label: "Mises à jour", icon: Megaphone },
    { to: "/admin/testimonials", label: "Témoignages", icon: Quote },
    { to: "/admin/support", label: "PAG Helper", icon: LifeBuoy },
  ] },
  { key: "marketplace", label: "Marketplace & Billetterie", icon: Store, items: [
    { to: "/admin/marketplace", label: "Marketplace (offres & validations)", icon: Store },
    { to: "/admin/billetterie", label: "Billetterie & Parcs partenaires", icon: TreePine },
    { to: "/admin/studio", label: "Atelier de modules (IA)", icon: Sparkles },
  ] },
  { key: "plateforme", label: "Plateforme & automatisation", icon: Zap, items: [
    { to: "/admin/metiers", label: "Modules métier", icon: Briefcase },
    { to: "/admin/automations", label: "Automations", icon: Zap },
    { to: "/admin/incidents", label: "Incidents", icon: AlertTriangle },
    { to: "/admin/integrations", label: "Intégrations", icon: Link2 },
    { to: "/admin/health", label: "Dette technique", icon: Activity },
    { to: "/admin/i18n", label: "Langues & devises", icon: Languages },
    { to: "/admin/baremes", label: "Barèmes réglementaires", icon: Scale },
    { to: "/admin/formations", label: "Formations", icon: GraduationCap },
  ] },
  { key: "securite", label: "Sécurité & conformité", icon: Shield, items: [
    { to: "/admin/audit", label: "Audit RGPD", icon: Shield },
    { to: "/admin/2fa", label: "Sécurité 2FA", icon: ShieldCheck },
    { to: "/admin/secure-connect", label: "SecureConnect", icon: KeyRound },
    { to: "/admin/team", label: "Équipe PAG", icon: Users },
    { to: "/admin/legal", label: "Mentions légales & CGV", icon: Gavel },
  ] },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const mobile = useMobileMenu();
  const { pathname } = useLocation();
  const { isStaff, canPath } = usePerms();
  const visibleTop = TOP_NAV.filter((item) => canPath(item.to));
  const visibleGroups = GROUPS.map((g) => ({ ...g, items: g.items.filter((item) => canPath(item.to)) }));
  const allowedHere = canPath(pathname);

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="dashboard-shell bg-white">
      {/* Sidebar */}
      <MobileTopBar portal="admin" label="Admin" open={mobile.open} onToggle={mobile.toggle} dark />
      <aside className={`${mobile.asideClass} border-r border-[#DEE2E6] bg-[#0A0A0A] text-white flex-col`} onClick={mobile.onNavClick} data-testid="admin-sidebar">
        <div className="p-6 border-b border-white/10 hidden lg:block">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-brand">
            <img src="/pag-logo.png" alt="PAG" className="h-9 w-9 object-contain bg-white p-0.5" />
            <span className="font-display font-black text-lg">PAG</span>
            <span className="text-xs uppercase tracking-[0.25em] text-white/50 border-l border-white/20 pl-3" data-testid="admin-brand-role">{isStaff ? "Équipe" : "Admin"}</span>
          </Link>
        </div>
        <GroupedNav portal="admin" top={visibleTop} groups={visibleGroups} dark />
        <div className="p-4 border-t border-white/10">
          <div className="text-xs uppercase tracking-[0.2em] text-white/50 mb-1">Connecté</div>
          <div className="font-mono text-xs mb-1">{user?.code}</div>
          {isStaff && <div className="text-[10px] uppercase tracking-wider font-bold text-[#FFD700] mb-3" data-testid="admin-staff-role">{user?.prenom} {user?.nom} · {user?.pag_role}</div>}
          {!isStaff && <div className="mb-3" />}
          <div className="mb-3"><LanguageCurrencySwitcher /></div>
          <Button
            onClick={doLogout}
            data-testid="admin-logout-btn"
            variant="outline"
            className="w-full border-white/20 bg-transparent text-white hover:bg-white hover:text-[#0A0A0A] h-9 text-xs uppercase tracking-[0.2em] font-semibold"
          >
            <LogOut className="h-3 w-3 mr-2" /> Déconnexion
          </Button>
          <div className="mt-4 pt-4 border-t border-white/10 text-[10px] text-white/50 space-y-0.5" data-testid="admin-support">
            <div className="uppercase tracking-[0.2em] font-semibold mb-1">Support</div>
            <a href="mailto:contact@parcavenue.eu" className="block hover:text-[#FFD700] transition-colors truncate">contact@parcavenue.eu</a>
            <a href="tel:+33970701792" className="block hover:text-[#FFD700] transition-colors font-mono">09 70 70 17 92</a>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="overflow-y-auto">
        <div className="sticky top-0 z-30 bg-white border-b border-[#DEE2E6] px-6 lg:px-10 py-3 flex items-center justify-between gap-4" data-testid="admin-topbar">
          <AdminGlobalSearch />
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#868E96] hidden lg:block">
            PAG Business Manager <span className="text-[#059669] ml-2">v2.0.05</span>
          </div>
        </div>
        <PortalIncidentBanner />
        <PushEnableBanner />
        {allowedHere ? <Outlet /> : (
          <div className="p-10 max-w-2xl" data-testid="admin-access-denied">
            <div className="border border-[#DEE2E6] p-8 bg-[#F8F9FA]">
              <div className="text-xs uppercase tracking-[0.3em] text-[#868E96] font-bold mb-3 flex items-center gap-2"><Lock className="h-3.5 w-3.5" /> Accès restreint</div>
              <h1 className="font-display text-3xl font-black tracking-tighter mb-3">Ce module n'est pas dans vos permissions</h1>
              <p className="text-sm text-[#495057]">Votre compte secondaire ({user?.pag_role}) n'a pas le droit « Voir » sur ce module. Demandez à l'administrateur principal d'ajuster votre matrice de permissions.</p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
